/**
 * ============================================================================
 * Web 前端监控日志模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Web 前端监控日志模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 📌 模块说明：
 * 本模块用于 **Web 管理后台前端** 的错误和性能日志。
 * 
 * ⚠️ 注意区分：
 * - 本模块 (web-logs)：Web 管理后台前端的日志（浏览器端）
 *   API 路径：/api/v1/web-logs/
 *   
 * - app-logs 模块：移动 App 端的日志（iOS/Android/小程序）
 *   API 路径：/api/v1/app-logs/
 * 
 * 📦 包含的功能：
 * - 仪表盘统计
 * - 错误日志查询与上传
 * - 性能日志查询与上传
 * - 用户分析（列表、画像、时间线）
 * - API 分析（统计、详情）
 * - 告警管理（检测、汇总）
 * 
 * 🎯 使用场景：
 * - Web 端：error-handler.ts 批量上报错误
 * - Web 端：performance-monitor.ts 批量上报性能数据
 * - Web 端：web-monitoring 模块的监控仪表盘页面
 * 
 * @module api/web-logs/factory
 * @generated Manual (AI-assisted)
 * @author YSFL Team
 * @created 2026-01-24
 * @updated 2026-01-25 添加仪表盘、用户分析、API分析、告警管理
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  // 响应类型
  WebAlertSummaryResponse,
  WebApiDetailResponse,
  WebApiStatisticsResponse,
  WebBehaviorLogResponse,
  WebBehaviorStatisticsResponse,
  WebDashboardResponse,
  WebErrorLogResponse,
  WebErrorStatisticsResponse,
  WebHotPageItem,
  WebLogUploadData,
  WebModuleUsageItem,
  WebPerformanceLogResponse,
  WebPerformanceStatisticsResponse,
  WebUserListItem,
  WebUserProfileResponse,
  WebUserTimelineEvent,
  // 请求类型
  BatchUploadWebBehaviorForm,
  BatchUploadWebErrorsForm,
  BatchUploadWebPerformanceForm,
  // 查询类型
  WebApiStatisticsQueryParams,
  WebBehaviorLogQueryParams,
  WebErrorLogQueryParams,
  WebHotPagesQueryParams,
  WebLogStatisticsQueryParams,
  WebModuleUsageQueryParams,
  WebPerformanceLogQueryParams,
  WebUserListQueryParams,
  WebUserProfileQueryParams,
  WebUserTimelineQueryParams,
  WebFunnelAnalysisQueryParams,
  WebFunnelAnalysisResponse,
  WebFunnelDefinition,
  WebFunnelDefinitionForm,
  WebFunnelDefinitionUpdateForm,
  WebInterruptionsQueryParams,
  WebInterruptionAnalysisResponse,
} from '../../types/web-logs'

// ===== 导入通用类型 =====
import type { PaginationResult, ApiResponse } from '../../types/api'

// ===== 导入请求客户端接口 =====
import type { IRequestClient } from '../core/client'

// ===== 导入路由定义 =====
import { WEB_LOGS_ROUTES } from './routes'

// ===== 导出工厂函数 =====

/**
 * 创建 Web 监控日志模块的 API 方法集合
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 * @returns API 方法集合对象
 * 
 * @example
 * ```typescript
 * // Web 端使用
 * import { createWebLogsApi } from '@bayes-mes/shared/api/web-logs'
 * const webLogsApi = createWebLogsApi(requestClient)
 * 
 * // 批量上传错误日志
 * const result = await webLogsApi.batchUploadErrorLogs({ logs: [...] })
 * 
 * // 批量上传性能日志
 * const perfResult = await webLogsApi.batchUploadPerformanceLogs({ logs: [...] })
 * ```
 */
export function createWebLogsApi(client: IRequestClient) {
  return {
    // =========================================================================
    // 错误日志
    // =========================================================================

    /**
     * 批量上传错误日志
     * 
     * 使用场景：
     * - error-handler.ts 批量上报前端错误
     * - 支持 Vue 组件错误、JS 运行时错误、Promise 错误、资源加载错误
     * 
     * @param data - 批量上传表单数据（snake_case）
     * @returns Promise<ApiResponse<WebLogUploadData>> 上传结果
     */
    batchUploadErrorLogs(data: BatchUploadWebErrorsForm) {
      return client.post<ApiResponse<WebLogUploadData>>(
        WEB_LOGS_ROUTES.errors.batch,
        data
      )
    },

    /**
     * 查询错误日志列表（分页）
     * 
     * 使用场景：
     * - Web 管理后台的错误日志列表页面
     * - 支持按设备、用户、错误类型、错误级别、时间范围等筛选
     * 
     * @param params - 查询参数（snake_case）
     * @returns Promise<PaginationResult<WebErrorLogResponse>> 分页结果
     */
    getErrorLogs(params?: WebErrorLogQueryParams) {
      return client.get<PaginationResult<WebErrorLogResponse>>(
        WEB_LOGS_ROUTES.errors.list,
        { params }
      )
    },

    /**
     * 获取错误日志统计
     * 
     * 使用场景：
     * - 监控仪表盘的错误统计卡片
     * - 显示错误数量、按类型/级别分组、趋势、热门错误
     * 
     * @param params - 查询参数（可选）
     * @returns Promise<ApiResponse<WebErrorStatisticsResponse>> 统计数据
     */
    getErrorStatistics(params?: WebLogStatisticsQueryParams) {
      return client.get<ApiResponse<WebErrorStatisticsResponse>>(
        WEB_LOGS_ROUTES.errors.statistics,
        { params }
      )
    },

    // =========================================================================
    // 性能日志
    // =========================================================================

    /**
     * 批量上传性能日志
     * 
     * 使用场景：
     * - performance-monitor.ts 批量上报前端性能数据
     * - 支持页面加载、Web Vitals、长任务、API 请求性能等
     * 
     * @param data - 批量上传表单数据（snake_case）
     * @returns Promise<ApiResponse<WebLogUploadData>> 上传结果
     */
    batchUploadPerformanceLogs(data: BatchUploadWebPerformanceForm) {
      return client.post<ApiResponse<WebLogUploadData>>(
        WEB_LOGS_ROUTES.performance.batch,
        data
      )
    },

    /**
     * 查询性能日志列表（分页）
     * 
     * 使用场景：
     * - Web 管理后台的性能日志列表页面
     * - 支持按设备、用户、指标类型、是否慢请求、时间范围等筛选
     * 
     * @param params - 查询参数（snake_case）
     * @returns Promise<PaginationResult<WebPerformanceLogResponse>> 分页结果
     */
    getPerformanceLogs(params?: WebPerformanceLogQueryParams) {
      return client.get<PaginationResult<WebPerformanceLogResponse>>(
        WEB_LOGS_ROUTES.performance.list,
        { params }
      )
    },

    /**
     * 获取性能日志统计
     * 
     * 使用场景：
     * - 监控仪表盘的性能统计卡片
     * - 显示总数、慢请求数、慢请求比例、Web Vitals、慢 API 排行
     * 
     * @param params - 查询参数（可选）
     * @returns Promise<ApiResponse<WebPerformanceStatisticsResponse>> 统计数据
     */
    getPerformanceStatistics(params?: WebLogStatisticsQueryParams) {
      return client.get<ApiResponse<WebPerformanceStatisticsResponse>>(
        WEB_LOGS_ROUTES.performance.statistics,
        { params }
      )
    },

    // =========================================================================
    // 仪表盘
    // =========================================================================

    /**
     * 获取仪表盘统计数据
     * 
     * 使用场景：
     * - Web 监控仪表盘首页
     * - 展示错误汇总、性能汇总、Web Vitals、趋势图
     * 
     * @param params - 查询参数（可选）
     * @returns Promise<ApiResponse<WebDashboardResponse>> 仪表盘数据
     */
    getDashboard(params?: WebLogStatisticsQueryParams) {
      return client.get<ApiResponse<WebDashboardResponse>>(
        WEB_LOGS_ROUTES.dashboard,
        { params }
      )
    },

    // =========================================================================
    // 用户分析
    // =========================================================================

    /**
     * 获取用户列表
     * 
     * 使用场景：
     * - Web 监控用户列表页面
     * - 展示活跃用户、操作次数、错误数等
     * 
     * @param params - 查询参数
     * @returns Promise<ApiResponse<any>> 用户列表（分页）
     */
    getUserList(params?: WebUserListQueryParams) {
      return client.get<ApiResponse<PaginationResult<WebUserListItem>>>(
        WEB_LOGS_ROUTES.users.list,
        { params }
      )
    },

    /**
     * 获取用户画像
     * 
     * 使用场景：
     * - Web 监控用户详情页面
     * - 展示用户的完整监控数据
     * 
     * @param userId - 用户ID
     * @param params - 查询参数（可选）
     * @returns Promise<ApiResponse<any>> 用户画像数据
     */
    getUserProfile(userId: number | string, params?: WebUserProfileQueryParams) {
      return client.get<ApiResponse<WebUserProfileResponse>>(
        WEB_LOGS_ROUTES.users.profile(userId),
        { params }
      )
    },

    /**
     * 获取用户时间线
     * 
     * 使用场景：
     * - Web 监控用户详情页面的时间线视图
     * - 展示用户的混合事件（性能+错误）
     * 
     * @param userId - 用户ID
     * @param params - 查询参数（可选）
     * @returns Promise<ApiResponse<any>> 用户时间线数据
     */
    getUserTimeline(userId: number | string, params?: WebUserTimelineQueryParams) {
      return client.get<ApiResponse<PaginationResult<WebUserTimelineEvent>>>(
        WEB_LOGS_ROUTES.users.timeline(userId),
        { params }
      )
    },

    // =========================================================================
    // API 分析
    // =========================================================================

    /**
     * 获取 API 统计分析
     * 
     * 使用场景：
     * - Web 监控 API 分析页面
     * - 展示 API 调用排行、趋势、小时分布
     * 
     * @param params - 查询参数
     * @returns Promise<ApiResponse<any>> API 统计数据
     */
    getApiStatistics(params?: WebApiStatisticsQueryParams) {
      return client.get<ApiResponse<WebApiStatisticsResponse>>(
        WEB_LOGS_ROUTES.api.statistics,
        { params }
      )
    },

    /**
     * 获取单个 API 详情分析
     * 
     * 使用场景：
     * - Web 监控 API 详情页面
     * - 展示单个 API 的详细统计数据
     * 
     * @param endpoint - API 端点名称
     * @param params - 查询参数（可选）
     * @returns Promise<ApiResponse<any>> API 详情数据
     */
    getApiDetail(endpoint: string, params?: {
      start_time?: string
      end_time?: string
    }) {
      return client.get<ApiResponse<WebApiDetailResponse>>(
        WEB_LOGS_ROUTES.api.detail,
        { params: { endpoint, ...params } }
      )
    },

    // =========================================================================
    // 告警管理
    // =========================================================================

    /**
     * 检测告警
     * 
     * 使用场景：
     * - 手动触发告警检测
     * - 获取实时告警状态
     * 
     * @param params - 查询参数（可选）
     * @returns Promise<ApiResponse<any>> 检测到的告警列表
     */
    detectAlerts(params?: WebLogStatisticsQueryParams) {
      return client.post<ApiResponse<{
        alerts: WebAlertSummaryResponse['currentAlerts']
        count: number
        detectedAt: string
      }>>(
        WEB_LOGS_ROUTES.alerts.detect,
        params
      )
    },

    /**
     * 获取告警汇总
     * 
     * 使用场景：
     * - Web 监控告警中心页面
     * - 展示当前告警、历史统计、趋势
     * 
     * @param params - 查询参数（可选）
     * @returns Promise<ApiResponse<any>> 告警汇总数据
     */
    getAlertSummary(params?: WebLogStatisticsQueryParams) {
      return client.get<ApiResponse<WebAlertSummaryResponse>>(
        WEB_LOGS_ROUTES.alerts.summary,
        { params }
      )
    },

    // =========================================================================
    // 行为日志分析
    // =========================================================================

    /**
     * 批量上传行为日志
     */
    batchUploadBehaviorLogs(data: BatchUploadWebBehaviorForm) {
      return client.post<ApiResponse<WebLogUploadData>>(
        WEB_LOGS_ROUTES.behavior.batch,
        data
      )
    },

    /**
     * 查询行为日志列表（分页）
     */
    getBehaviorLogs(params?: WebBehaviorLogQueryParams) {
      return client.get<PaginationResult<WebBehaviorLogResponse>>(
        WEB_LOGS_ROUTES.behavior.list,
        { params }
      )
    },

    /**
     * 获取行为统计
     */
    getBehaviorStatistics(params?: WebLogStatisticsQueryParams) {
      return client.get<ApiResponse<WebBehaviorStatisticsResponse>>(
        WEB_LOGS_ROUTES.behavior.statistics,
        { params }
      )
    },

    /**
     * 获取热门页面
     */
    getHotPages(params?: WebHotPagesQueryParams) {
      return client.get<ApiResponse<WebHotPageItem[]>>(
        WEB_LOGS_ROUTES.behavior.hotPages,
        { params }
      )
    },

    /**
     * 获取模块使用统计
     */
    getModuleUsage(params?: WebModuleUsageQueryParams) {
      return client.get<ApiResponse<WebModuleUsageItem[]>>(
        WEB_LOGS_ROUTES.behavior.modules,
        { params }
      )
    },

    // =========================================================================
    // 漏斗分析
    // =========================================================================

    /**
     * 获取漏斗定义列表
     */
    getFunnelList() {
      return client.get<ApiResponse<WebFunnelDefinition[]>>(
        WEB_LOGS_ROUTES.funnel.list
      )
    },

    /**
     * 漏斗转化分析
     */
    getFunnelAnalysis(params: WebFunnelAnalysisQueryParams) {
      return client.get<ApiResponse<WebFunnelAnalysisResponse>>(
        WEB_LOGS_ROUTES.funnel.analysis,
        { params }
      )
    },

    /**
     * 操作中断分析
     */
    getFunnelInterruptions(params?: WebInterruptionsQueryParams) {
      return client.get<ApiResponse<WebInterruptionAnalysisResponse>>(
        WEB_LOGS_ROUTES.funnel.interruptions,
        { params }
      )
    },

    /**
     * 创建漏斗定义
     */
    createFunnel(data: WebFunnelDefinitionForm) {
      return client.post<ApiResponse<WebFunnelDefinition>>(
        WEB_LOGS_ROUTES.funnel.create,
        data
      )
    },

    /**
     * 更新漏斗定义
     */
    updateFunnel(id: number | string, data: WebFunnelDefinitionUpdateForm) {
      return client.put<ApiResponse<WebFunnelDefinition>>(
        WEB_LOGS_ROUTES.funnel.update(id),
        data
      )
    },

    /**
     * 删除漏斗定义
     */
    deleteFunnel(id: number | string) {
      return client.delete<ApiResponse<null>>(
        WEB_LOGS_ROUTES.funnel.remove(id)
      )
    },
  }
}

/**
 * Web 监控日志 API 类型
 */
export type WebLogsApi = ReturnType<typeof createWebLogsApi>
